/**
 * Generic, zero-React vanilla external store for the opener/unwinder live
 * tables. Read via `useSyncExternalStore` (see `useLiveStore.ts`).
 *
 * High-frequency WS frames mutate this store in plain JS — no React state,
 * no full Map copy. Only rows subscribed to a *changed* key re-render. The
 * sorted id list (used by the tab + virtualizer) recomputes on a throttled,
 * rAF-coalesced cadence and returns a referentially stable array when the
 * order is unchanged.
 *
 * The store is deliberately generic over `T` (the live-state object —
 * `EnrichedEntryStatus` for the opener, `EnrichedRuleStatus` for the
 * unwinder). Each service supplies its own:
 *   - `keysOf(item)` — the keys the item is registered under (dual-keying:
 *     both `ruleId` and `pairId`, mirroring the existing `pairId ?? ruleId`
 *     lookup order).
 *   - `equals(a, b)` — structural comparison over the rendered fields used to
 *     skip no-op notifies.
 *   - `merge(prev, next)` — optional; ports the unwinder's field-preservation
 *     (`withPreservedFields`) so streams that omit a field don't clobber it.
 *
 * Edge cases / invariants:
 *   - No-tear: `getKeySnapshot` returns a cached, referentially stable value
 *     between real changes — a new ref ONLY after the key's version bumps.
 *   - Dual keying: one object is stored under every key in `keysOf`; a change
 *     bumps the version + notifies the listeners of ALL its keys.
 *   - Snapshot prune: `applySnapshot` is full-replace — keys absent from the
 *     incoming batch are evicted and their listeners notified.
 *
 * Notification coalescing (2026-08-07):
 *   Values are written SYNCHRONOUSLY; only the NOTIFY is deferred to the next
 *   animation frame. That ordering is the invariant the whole scheme rests on:
 *   `getKeySnapshot` returns the new value from the instant of the write, so
 *   `useSyncExternalStore`'s contract (after the subscribe callback fires,
 *   `getSnapshot` must return the new value) holds, and pollers that read
 *   snapshots without listening — `waitForLiveSettle` — are unaffected. The
 *   inverse (notifying before writing) would be a bug.
 *
 *   While the document is hidden no flush is scheduled at all: writes keep
 *   landing so the store stays current, but no STORE SUBSCRIBER repaints.
 *   `flushPending()` lets the owning hook repaint on `visibilitychange`. The
 *   DOM listener deliberately lives in the hooks (which already have
 *   `useEffect` cleanup) rather than here — this store has no teardown hook to
 *   unregister it.
 *
 *   SCOPE, precisely: this gates only what flows through `notify`. The page
 *   tree above the rows still re-renders per socket message, because
 *   `useSocketEvent` bumps a `useState` on every frame it delivers and neither
 *   `EntryTabByPath` nor `UnwindingTabSumOfSides` is memoized. So "hidden tab
 *   does no work" is NOT true today — only "hidden tab runs no row renders".
 *   Closing the rest means taking the high-frequency events off
 *   `useSocketEvent`'s `lastMessage` state — spec'd separately in
 *   arbitrage-core as `docs/specs/opener-socket-event-rerender.md`.
 */

/**
 * Throttle (ms) for the *order* recompute only — never for cell values.
 *
 * Cadence contract: per-key cell state (`useLiveState` →
 * `subscribeKey`/`getKeySnapshot`) is written at FULL WS rate and the
 * NOTIFICATION is coalesced into the next animation frame (see
 * "Notification coalescing" below); this throttle gates ONLY the sorted id
 * list (`subscribeOrder` / `getOrderSnapshot` /
 * `rankOf`), which is a paint-cost concern (virtualizer + row reflow), not a
 * freshness one. The order recomputes at most once per window, rAF-coalesced.
 *
 * 500ms is the tuned default: long enough that a burst of frames collapses
 * into a single reflow (the whole point), short enough that a genuine rank
 * change surfaces within ~half a second — below the threshold where an
 * operator perceives the list as "stuck". Lower values reintroduce reflow
 * jitter under load; higher values make metric-sorts feel laggy. Exposed as a
 * mutable export so a future tuning pass (or a test) can adjust it without a
 * signature change, but it is intentionally NOT wired to a config knob —
 * there is one cadence for both mirrored services.
 */
export const ORDER_THROTTLE_MS = 500;

export type Comparator = (a: string, b: string) => number;

export interface LiveStoreConfig<T> {
  /**
   * Keys an item is registered under. Mirror the existing dual-keying:
   * `[ruleId, pairId]` (deduped, falsy dropped). The FIRST key is treated as
   * the canonical key for storage of the object itself; all keys point at the
   * same object.
   */
  keysOf: (item: T) => string[];
  /**
   * Structural comparison over the rendered fields. Return `true` when `next`
   * is render-equivalent to `prev` so the update can be skipped (no notify).
   */
  equals: (prev: T, next: T) => boolean;
  /**
   * Optional merge applied before the equality check + store. Receives the
   * current value (if any) for the item's canonical key and the incoming
   * item; returns the value to store. Used to port the unwinder's
   * field-preservation. When omitted, the incoming item is stored verbatim.
   */
  merge?: (prev: T | undefined, next: T) => T;
  /**
   * Optional injectable clock + frame scheduler. Production leaves this
   * unset (resolves `performance.now` / `requestAnimationFrame` lazily at
   * call time). Tests inject a deterministic clock so the order throttle is
   * fully controllable.
   */
  scheduler?: Scheduler;
  /**
   * Whether the surface is currently not visible. While `true` no notify
   * flush is scheduled — writes still land, nothing repaints. Production
   * leaves this unset (resolves `document.hidden` lazily at call time, like
   * `scheduler`); tests inject a deterministic predicate.
   *
   * This is deliberately NOT left to rAF's own hidden-tab pausing. That
   * pausing is a browser convention, not a specified guarantee, and it is
   * invisible in tests; an explicit predicate is assertable and cannot be
   * silently changed by a browser. (It also covers the `setTimeout` fallback
   * in `defaultScheduler`, though in practice every environment that has a
   * `document` also has `requestAnimationFrame`, so that branch is
   * unreachable in a real browser.)
   */
  isHidden?: () => boolean;
}

export interface Scheduler {
  now(): number;
  /** Schedule a frame callback; returns a handle for cancellation. */
  requestFrame(cb: () => void): number;
  cancelFrame(handle: number): void;
}

export interface OrderInputs {
  comparator: Comparator;
  /** When `true`, the order is not recomputed (cells keep ticking). */
  freeze?: boolean;
}

export interface LiveStore<T> {
  /** Merge-style update: changed keys only. No full Map copy. */
  applyUpdate(items: T[]): void;
  /** Full-replace snapshot: evicts keys not present in `items`. */
  applySnapshot(items: T[]): void;
  /**
   * Eviction ONLY: drop stored values whose key is not in `keys`, touching no
   * value that stays. The cheap half of `applySnapshot` — it exists so the
   * periodic prune can be a list of ids instead of a re-broadcast of every
   * state on the page.
   */
  retainKeys(keys: string[]): void;

  /** `useSyncExternalStore` per-key contract. */
  subscribeKey(id: string, cb: () => void): () => void;
  getKeySnapshot(id: string): T | undefined;

  /** `useSyncExternalStore` order contract (sorted id list). */
  subscribeOrder(cb: () => void): () => void;
  getOrderSnapshot(): string[];

  /**
   * Rank of ANY registered key (canonical OR non-canonical) in the current
   * throttled order — the first-class affordance both tabs use to stable-sort
   * their OWN react-query membership list. `getOrderSnapshot()` returns only
   * canonical keys; `rankOf` additionally resolves the non-canonical key
   * (e.g. a `pairId` when the item canonicalized under its `ruleId`) to the
   * same rank, so a consumer can call `rankOf(pairId ?? ruleId)` regardless of
   * which key the store happened to canonicalize on.
   *
   * O(1): reads an internal key→rank Map rebuilt only when the order
   * recomputes (NOT per call). Unknown ids — including visible rows the store
   * holds no envelope for — return `Number.POSITIVE_INFINITY` so they
   * stable-sort to the tail without vanishing.
   */
  rankOf(id: string): number;

  /** Set the sort comparator / freeze flag from the tab. */
  setOrderInputs(inputs: OrderInputs): void;

  /**
   * Flush any coalesced notifications immediately instead of waiting for the
   * next frame. Called by the owning hook when the document becomes visible
   * again, so a tab that accumulated frames while hidden repaints at once
   * rather than on the next incoming frame. No-op when nothing is pending.
   */
  flushPending(): void;

  /** Test/diagnostic helpers. */
  size(): number;
  has(id: string): boolean;
}

interface KeyEntry<T> {
  /** The current stored object (shared across all keys of the same item). */
  value: T;
  /** Bumped on every real change to this key. Drives no-tear caching. */
  version: number;
  /** Listeners subscribed to this key. */
  listeners: Set<() => void>;
  /** Last object handed out by `getKeySnapshot` (referentially stable). */
  cachedSnapshot: T | undefined;
  /** Version at which `cachedSnapshot` was computed. */
  cachedVersion: number;
}

/**
 * Default scheduler — resolved LAZILY at call time (not module-eval) so a
 * non-DOM/test environment that stubs the globals later is respected, and so
 * we degrade gracefully when `requestAnimationFrame` is unavailable.
 */
const defaultScheduler: Scheduler = {
  now: () =>
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now(),
  requestFrame: cb =>
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame(cb)
      : (setTimeout(cb, 0) as unknown as number),
  cancelFrame: handle => {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle);
    else clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
  }
};

/**
 * Default visibility predicate — resolved LAZILY at call time (same reasoning
 * as `defaultScheduler`). A non-DOM environment is never "hidden", so the
 * store behaves exactly as it did before in tests that don't opt in.
 */
const defaultIsHidden = (): boolean =>
  typeof document !== 'undefined' && document.hidden === true;

export function createLiveStore<T>(config: LiveStoreConfig<T>): LiveStore<T> {
  const { keysOf, equals, merge } = config;
  const scheduler = config.scheduler ?? defaultScheduler;
  const isHidden = config.isHidden ?? defaultIsHidden;
  const now = () => scheduler.now();
  const raf = (cb: () => void) => scheduler.requestFrame(cb);
  const cancelRaf = (handle: number) => scheduler.cancelFrame(handle);

  /** key -> entry. A single item is stored under each of its keys. */
  const entries = new Map<string, KeyEntry<T>>();

  // ── Order state ──────────────────────────────────────────────────────────
  let orderInputs: OrderInputs | null = null;
  const orderListeners = new Set<() => void>();
  let orderSnapshot: string[] = [];
  /**
   * key → rank index covering EVERY key of each ranked item (both canonical
   * and non-canonical). Rebuilt in lock-step with `orderSnapshot` whenever the
   * order recomputes, so `rankOf` is O(1) and never recomputes per call.
   */
  let rankIndex = new Map<string, number>();
  let orderDirty = false;
  let lastRecomputeAt = 0;
  let rafHandle: number | null = null;
  let timerHandle: ReturnType<typeof setTimeout> | null = null;

  // ── Notification coalescing ──────────────────────────────────────────────
  /**
   * Listeners queued for the next frame. Per-key AND order listeners share
   * this one queue, so whenever both are dirty within the same frame they
   * flush together.
   *
   * That is NOT a same-frame guarantee, and do not write one into a test: the
   * order recompute is itself a frame hop (`scheduleOrderRecompute` → `run` →
   * `recomputeOrder` → `notify`), so a value change typically repaints its
   * cells one frame — and, inside the throttle window, several — before the
   * rank catches up. Cells leading the reorder is the harmless direction; the
   * shared queue exists so there is ONE flush mechanism, not two.
   */
  const pendingListeners = new Set<() => void>();
  let notifyFrameHandle: number | null = null;

  // ── Key helpers ──────────────────────────────────────────────────────────

  const getOrCreateEntry = (key: string, value: T): KeyEntry<T> => {
    let e = entries.get(key);
    if (!e) {
      e = {
        value,
        version: 0,
        listeners: new Set(),
        cachedSnapshot: undefined,
        cachedVersion: -1
      };
      entries.set(key, e);
    }
    return e;
  };

  /**
   * Write `value` under every key in `keys`, bump versions, and collect
   * listeners to notify. Returns the set of unique listeners (caller flushes).
   */
  const writeItem = (keys: string[], value: T, toNotify: Set<() => void>) => {
    for (const key of keys) {
      const e = getOrCreateEntry(key, value);
      e.value = value;
      e.version += 1;
      for (const l of e.listeners) toNotify.add(l);
    }
  };

  /**
   * Run every pending listener exactly once and clear the queue.
   *
   * The batch is copied BEFORE invoking: a listener may subscribe or
   * unsubscribe while it runs (React re-renders synchronously inside the
   * `useSyncExternalStore` callback), and mutating `pendingListeners` mid-
   * iteration would either skip a listener or run a newly-queued one a frame
   * early.
   */
  const flushPendingNotifications = () => {
    notifyFrameHandle = null;
    if (pendingListeners.size === 0) return;
    const batch = [...pendingListeners];
    pendingListeners.clear();
    for (const l of batch) l();
  };

  const scheduleNotifyFlush = () => {
    if (notifyFrameHandle != null) return; // already scheduled for this frame
    // Hidden — accumulate in `pendingListeners`; `flushPending()` (called by
    // the owning hook on `visibilitychange`) repaints when we come back.
    if (isHidden()) return;
    notifyFrameHandle = raf(flushPendingNotifications);
  };

  /**
   * Queue listeners for the next frame. Coalescing here is what turns a burst
   * of N socket frames touching the same row into ONE render instead of N.
   */
  const notify = (listeners: Set<() => void>) => {
    // Nothing subscribed (common for `orderListeners` on a page that never
    // reads the order) — do not burn a frame on a flush with no work.
    if (listeners.size === 0) {
      // ...but if a queue was left stranded (entries added while hidden, and
      // no `flushPending` since), re-arm here rather than waiting for the next
      // non-empty notify. `scheduleNotifyFlush` is itself a no-op when a frame
      // is already pending or the document is still hidden.
      if (pendingListeners.size > 0) scheduleNotifyFlush();
      return;
    }
    for (const l of listeners) pendingListeners.add(l);
    scheduleNotifyFlush();
  };

  const flushPending = () => {
    if (notifyFrameHandle != null) {
      cancelRaf(notifyFrameHandle);
      notifyFrameHandle = null;
    }
    flushPendingNotifications();
  };

  // ── Order recompute ──────────────────────────────────────────────────────

  const distinctIds = (): string[] => {
    // An item is stored under multiple keys; collapse to one id per item.
    // Use the canonical (first) key of each value. We dedupe by value
    // identity since all keys of an item share the same object reference.
    const seen = new Set<T>();
    const ids: string[] = [];
    for (const [key, entry] of entries) {
      // Skip bare placeholders (subscribed before a value landed).
      if ((entry.value as unknown) === undefined) continue;
      if (seen.has(entry.value)) continue;
      seen.add(entry.value);
      ids.push(key);
    }
    return ids;
  };

  const arraysEqual = (a: string[], b: string[]): boolean => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  /**
   * Rebuild the key→rank index from the freshly-sorted canonical ids. For each
   * canonical id at rank `i`, map ALL keys of its value object to `i` (so a
   * non-canonical `pairId` resolves to the same rank as its canonical
   * `ruleId`). Cheap: one pass over `entries` building a value→keys[] reverse
   * map, then one pass over the sorted ids.
   */
  const rebuildRankIndex = (sortedIds: string[]) => {
    const keysByValue = new Map<T, string[]>();
    for (const [key, entry] of entries) {
      if ((entry.value as unknown) === undefined) continue;
      const arr = keysByValue.get(entry.value);
      if (arr) arr.push(key);
      else keysByValue.set(entry.value, [key]);
    }
    const next = new Map<string, number>();
    for (let i = 0; i < sortedIds.length; i++) {
      const value = entries.get(sortedIds[i])?.value;
      if ((value as unknown) === undefined) continue;
      const keys = keysByValue.get(value as T);
      if (!keys) continue;
      for (const k of keys) next.set(k, i);
    }
    rankIndex = next;
  };

  const recomputeOrder = () => {
    lastRecomputeAt = now();
    orderDirty = false;
    if (!orderInputs) return;
    const ids = distinctIds();
    ids.sort(orderInputs.comparator);
    if (!arraysEqual(ids, orderSnapshot)) {
      orderSnapshot = ids;
      rebuildRankIndex(ids);
      notify(orderListeners);
    }
  };

  const clearScheduled = () => {
    if (rafHandle != null) {
      cancelRaf(rafHandle);
      rafHandle = null;
    }
    if (timerHandle != null) {
      clearTimeout(timerHandle);
      timerHandle = null;
    }
  };

  /**
   * Mark the order dirty and schedule a throttled, rAF-coalesced recompute.
   * Rapid calls collapse into a single recompute per `ORDER_THROTTLE_MS`.
   * Frozen → no-op.
   */
  const scheduleOrderRecompute = () => {
    if (!orderInputs || orderInputs.freeze) return;
    orderDirty = true;
    if (rafHandle != null || timerHandle != null) return; // already scheduled

    const elapsed = now() - lastRecomputeAt;
    const run = () => {
      rafHandle = null;
      timerHandle = null;
      if (orderDirty) recomputeOrder();
    };

    if (elapsed >= ORDER_THROTTLE_MS) {
      // Throttle window elapsed — coalesce within the next animation frame.
      rafHandle = raf(run);
    } else {
      // Within the window — wait out the remainder, then run.
      timerHandle = setTimeout(() => {
        timerHandle = null;
        // Prefer rAF for the actual paint-adjacent recompute when available.
        rafHandle = raf(run);
      }, ORDER_THROTTLE_MS - elapsed);
    }
  };

  // ── Public API ───────────────────────────────────────────────────────────

  const applyUpdate = (items: T[]) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const toNotify = new Set<() => void>();
    // `changed` tracks whether ANY value/key-set changed — independent of
    // whether a per-key listener happens to be subscribed. A freshly-added
    // pair with no row mounted yet still has to enter the order list.
    let changed = false;

    for (const incoming of items) {
      const keys = keysOf(incoming).filter(Boolean);
      if (keys.length === 0) continue;

      const canonicalKey = keys[0];
      const prev = entries.get(canonicalKey)?.value;
      const merged = merge ? merge(prev, incoming) : incoming;

      // `prev` may be `undefined` either because the key is brand new OR
      // because a listener subscribed before any value landed (placeholder
      // entry). Treat a placeholder (value === undefined) as a real add.
      if (prev !== undefined && equals(prev, merged)) {
        // Render-equivalent — skip entirely (no notify, no version bump).
        continue;
      }

      changed = true;
      writeItem(keys, merged, toNotify);
    }

    if (toNotify.size > 0) notify(toNotify);
    if (changed) {
      // A changed value may alter sort position (e.g. tna/name) and a new
      // key alters the id set — recompute order in both cases (throttled).
      scheduleOrderRecompute();
    }
  };

  const applySnapshot = (items: T[]) => {
    if (!Array.isArray(items)) return;
    const toNotify = new Set<() => void>();
    let changed = false;

    // Build the set of keys present in the incoming batch.
    const presentKeys = new Set<string>();
    for (const incoming of items) {
      const keys = keysOf(incoming).filter(Boolean);
      if (keys.length === 0) continue;
      const canonicalKey = keys[0];
      const prev = entries.get(canonicalKey)?.value;
      const merged = merge ? merge(prev, incoming) : incoming;

      for (const k of keys) presentKeys.add(k);

      if (prev !== undefined && equals(prev, merged)) {
        // Unchanged — but the value object must stay current so eviction
        // dedupe (by value identity) still collapses its keys. Keep the
        // existing stored object; no notify.
        continue;
      }
      changed = true;
      writeItem(keys, merged, toNotify);
    }

    // Evict keys no longer present.
    for (const key of [...entries.keys()]) {
      if (presentKeys.has(key)) continue;
      const e = entries.get(key)!;
      for (const l of e.listeners) toNotify.add(l);
      entries.delete(key);
      changed = true;
    }

    if (toNotify.size > 0) notify(toNotify);
    // Any add / change / eviction touches the id set — recompute order.
    if (changed) scheduleOrderRecompute();
  };

  /**
   * Evict the keys the server no longer lists. The prune half of
   * `applySnapshot` without its payload: the periodic full snapshot existed
   * only to drive this eviction, and re-sending every state twice a minute to
   * express "these ids are gone" is what buried the socket's in-band ping.
   *
   * Two deliberate differences from `applySnapshot`'s eviction:
   *  - An entry with NO value yet is kept. `subscribeKey` creates one when a
   *    row mounts before its first frame; it holds nothing stale to prune, and
   *    deleting it drops that row's listener set — the row would go silent for
   *    the rest of its life.
   *  - An EMPTY key list is a no-op. "The server currently knows of nothing"
   *    is not the same claim as "everything you hold is gone", and acting on
   *    it would blank the whole table off one bad frame. A genuinely empty
   *    page is corrected by the seed on the next reconnect.
   */
  const retainKeys = (keys: string[]) => {
    if (!Array.isArray(keys) || keys.length === 0) return;
    const keep = new Set(keys);
    const toNotify = new Set<() => void>();
    let changed = false;

    for (const key of [...entries.keys()]) {
      if (keep.has(key)) continue;
      const e = entries.get(key);
      if (!e || e.value === undefined) continue;
      for (const l of e.listeners) toNotify.add(l);
      entries.delete(key);
      changed = true;
    }

    if (toNotify.size > 0) notify(toNotify);
    if (changed) scheduleOrderRecompute();
  };

  const subscribeKey = (id: string, cb: () => void): (() => void) => {
    let e = entries.get(id);
    if (!e) {
      // Allow subscribing to a key before its first value lands (rows mount
      // off the order list / react-query before the WS frame arrives).
      e = {
        value: undefined as unknown as T,
        version: 0,
        listeners: new Set(),
        cachedSnapshot: undefined,
        cachedVersion: -1
      };
      entries.set(id, e);
    }
    e.listeners.add(cb);
    return () => {
      // Drop it from the pending queue too: a listener that unsubscribed
      // between the schedule and the flush must not be invoked.
      pendingListeners.delete(cb);
      const entry = entries.get(id);
      if (!entry) return;
      entry.listeners.delete(cb);
    };
  };

  const getKeySnapshot = (id: string): T | undefined => {
    const e = entries.get(id);
    if (!e) return undefined;
    if (e.cachedVersion !== e.version) {
      e.cachedSnapshot = e.value;
      e.cachedVersion = e.version;
    }
    return e.cachedSnapshot;
  };

  const subscribeOrder = (cb: () => void): (() => void) => {
    orderListeners.add(cb);
    return () => {
      pendingListeners.delete(cb);
      orderListeners.delete(cb);
    };
  };

  const getOrderSnapshot = (): string[] => orderSnapshot;

  const rankOf = (id: string): number => {
    const rank = rankIndex.get(id);
    return rank === undefined ? Number.POSITIVE_INFINITY : rank;
  };

  const setOrderInputs = (inputs: OrderInputs) => {
    const wasFrozen = orderInputs?.freeze === true;
    orderInputs = inputs;
    if (inputs.freeze) {
      // Stop any pending recompute; cells keep ticking, order is held.
      clearScheduled();
      return;
    }
    // Comparator changed or just unfrozen — recompute now (subject to
    // throttle scheduling). Unfreeze forces a fresh schedule.
    if (wasFrozen) orderDirty = true;
    scheduleOrderRecompute();
  };

  const size = (): number => distinctIds().length;
  const has = (id: string): boolean => {
    const e = entries.get(id);
    // A bare placeholder entry (subscribed before any value landed) has an
    // `undefined` value — report `false` until a real value is stored.
    return e !== undefined && (e.value as unknown) !== undefined;
  };

  return {
    applyUpdate,
    applySnapshot,
    retainKeys,
    subscribeKey,
    getKeySnapshot,
    subscribeOrder,
    getOrderSnapshot,
    rankOf,
    setOrderInputs,
    flushPending,
    size,
    has
  };
}
